import * as mobx from "mobx";
import { makeObservable } from "mobx";
import { action, computed, observable } from 'mobx';
import { stallUnresolvedPromises } from "../../util/async";
import BrandConfigStore from "../config/BrandConfigStore";
import {upsertUserPreferences, userPreferences} from "../users/usersApi";
import './debugger';
import LoginStore from "./LoginStore";
import './logoutOnUnauthorized';
import * as SessionApi from "./SessionApi";

/**
 * I contain Lightweight things that should always be accessible when a user is logged in
 * - the user
 * - the users account
 * - the brand config
 */
export class SessionStore {
  sessionLoaded = false;

  /** @type {string | undefined} */
  sessionToken = undefined;

  /** @type {User} */
  user = undefined;

  /** @type {UserPreferences} */
  userPreferences = undefined;

  // deprecated, use the BrandConfigStore
  /** @type {BrandConfig} */
  get brandConfig() {
    return BrandConfigStore.brandConfig
  }

  /** @type {Account} */
  account = undefined;

  bootStarted = false;

  constructor() {
    makeObservable(this, {
      sessionLoaded: observable,
      sessionToken: observable,
      user: observable,
      userPreferences: observable,
      brandConfig: computed,
      account: observable,
      _loadSession: action,
      _setLoaded: action,
      assignUserPreferencesWithDefaults: action,
      reloadPreferences: action,
      unload: action,
      patchUserPreferences: action
    });

    mobx.reaction(
      () => LoginStore.token,
      token => {
        if (token) {
          this._loadSession(token);
        } else {
          this.unload()
        }
      },
      { fireImmediately: true }
    )
  }

  _loadSession(token) {
    if (!token) {
      throw new Error('token must be specified')
    }
    LoginStore.runUsingCachedResult({
      key: "SessionStore.getUsersAndAccounts",
      promise: this._getUsersAndAccount(),
      callback: action(([user, userPreferences, account]) => {
        this._setLoaded({ account, userPreferences, user });
      })
    });
  }

  _setLoaded({account, userPreferences, user}) {
    this.account = account;
    this.user = user;
    this.assignUserPreferencesWithDefaults(userPreferences);
    this.sessionLoaded = true;
  }

  _getUsersAndAccount = stallUnresolvedPromises(() => {
    return Promise.all([
      SessionApi.getCurrentUser(),
      userPreferences().then(prefs => prefs.data),
      SessionApi.getCurrentUsersAccount(),
      mobx.when(() => !!BrandConfigStore.brandConfig)
    ]);
  });

  assignUserPreferencesWithDefaults = (userPreferences) => {
    this.userPreferences = {
      stickyUrl: false,
      // set some default values since mobx doesn't like detecting changes
      // when new fields are added
      ...userPreferences
    }
  };

  reloadPreferences() {
    this.userPreferences = undefined;
    userPreferences().then(this.assignUserPreferencesWithDefaults);
  }

  unload() {
    const redirectUrl = this.brandConfig && this.brandConfig.logoutRedirectUrl;
    this.sessionLoaded = false;
    this.account = undefined;
    this.user = undefined;
    this.userPreferences = undefined;
    if (redirectUrl) {
      window.location = redirectUrl
    }
  }

  /**
   * Convenience method for accessing the current accounts reports service.
   * Note that the reportsService is set from the root account. The server response
   * fills this in for the currentAccount endpoint
   */
  get reportsService() {
    return this.account && this.account.services.reportsService;
  }

  onceUnloaded = () => {
    return mobx.when(() => !this.sessionLoaded)
  };

  onceLoaded = () => {
    return mobx.when(() => this.sessionLoaded)
  };

  impersonateUser(username) {
    const target = window.open(window.assetUrl + '/loading.html', '_blank');
    return SessionApi.generateSSOToken(username).then((resp) => {
      target.location = `/#/login?iFrame=false&ssoToken=${resp.data.ssoToken}`;
    })
  }

  hasUserPermission = (permission) => {
    return this.user && this.user.permissions.includes(permission);
  }

  /** patches user preferences and flushes them to the server
   * @param {Partial.<UserPreferences>} updates
   * @returns {Promise.<void>}
   */
  patchUserPreferences({ userId, userPreferences }) {
    if (userId === this.user.userId) {
      Object.assign(this.userPreferences, userPreferences)
    }
    return upsertUserPreferences({ userId, userPreferences })
  }

}

/**
 * @typedef {object} AccountServices - grab bag of enabled features for the customer
 * @property {boolean} chatExecListingManagement
 * @property {boolean} localPages
 * @property {boolean} listingManagement
 * @property {boolean} bingListingManagement
 * @property {boolean} appleListingManagement
 * @property {boolean} yelpListingManagement
 * @property {boolean} googleListingManagement
 * @property {boolean} facebookListingManagement
 * @property {boolean} yelpApi
 * @property {boolean} socialPublishing
 * @property {boolean} socialEngagement
 * @property {boolean} socialCommenting
 * @property {boolean} sendiblePublishing
 * @property {object} reportsService
 */
/**
 *
 * @typedef {object} Account
 * @property {string} name,
 * @property {AccountServices} services
 * @property {string} [parentAccountId]
 * @property {string} billingAccountId
 * @property {string} clientAccountId
 * @property {string} accountId
 *
 */


const defaultSessionStore = new SessionStore();

export default defaultSessionStore;
